import { action, computed, observable } from 'mobx';

import { PieSeriesData } from 'components/Graph/PieGraph';
import { convertPiechartData } from 'helpers/chart';

import { RootStore } from 'stores/rootStore';
import { API_ACCOUNT_DETAIL_HISTORY, API_ACCOUNT_DETAIL_PLAN_HISTORY } from 'api/endpoints';
import {
  AccountEditPlan,
  SimpleBucket,
  Bucket,
  BucketType,
} from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { DevelopmentGraphData } from 'components/Graph/DevelopmentLineGraph';
import { Currency } from 'enums';
import { ChartPeriods } from 'common/interfaces';

export enum BucketTypes {
  'SHORT_TERM' = 'K1',
  'MID_TERM' = 'K2',
  'LONG_TERM' = 'K3',
  'TOTAL' = 'total',
}

const BucketOptions = [BucketType.TOTAL, BucketType.LONG_TERM, BucketType.MID_TERM, BucketType.SHORT_TERM];

//const mock = require('./mock.json');

interface ExtSimpleBucket extends SimpleBucket {
  profit?: number;
}

export class DevelopmentStore {
  private rootStore: RootStore;
  private accountId: string;

  @observable data: AccountEditPlan | null = null;
  @observable graphData: DevelopmentGraphData | null = null;
  @observable period = ChartPeriods.ALL_TIME;
  @observable percentage = 0;
  @observable activeBucket = 0;
  @observable activeBucketType = BucketTypes.TOTAL;
  @observable investments: number | null = null;
  @observable balance: number | null = null;
  @observable yearlyProfit: number | null = null;
  @observable simpleYearlyProfit: number | null = null; // FIXME
  @observable profitValue: number | null = null;
  @observable activeBuckets: Bucket[] = [];
  @observable bucketTabs: ExtSimpleBucket[] = [];

  @observable dataReady = false;

  constructor(rootStore: RootStore, accountId: string) {
    this.rootStore = rootStore;
    this.accountId = accountId;
  }

  @action setActiveBucket = (value: number, type?: BucketType) => {
    this.activeBucket = value;
    this.activeBucketType = type ? BucketTypes[type] : BucketTypes[BucketOptions[value]];
    this.yearlyProfit = this.graphData?.yearlyProfit ? this.graphData.yearlyProfit[this.activeBucketType] : null;
    /*
    this.simpleYearlyProfit =
      this.graphData?.simpleYearlyProfit && this.graphData?.simpleYearlyProfit[this.activeBucketType]
        ? this.graphData.simpleYearlyProfit[this.activeBucketType][this.period]
        : null;
    */
    const lastDP = this.graphData?.percentages[this.activeBucketType]?.length;
    this.simpleYearlyProfit = lastDP ? this.graphData!.percentages[this.activeBucketType]![lastDP - 1].value : null;

    this.balance = this.bucketTabs[value]?.balance.value || 0;
    this.profitValue = this.bucketTabs[value]?.profit || null;
    this.investments =
      this.graphData && Array.isArray(this.graphData.investments[this.activeBucketType])
        ? this.graphData.investments[this.activeBucketType]![
            this.graphData.investments[this.activeBucketType]!.length - 1
          ].value.value
        : 0;
  };

  @action setPeriod = (value: ChartPeriods) => {
    this.period = value;
    /*
    this.simpleYearlyProfit =
      this.graphData?.simpleYearlyProfit && this.graphData?.simpleYearlyProfit[this.activeBucketType]
        ? this.graphData.simpleYearlyProfit[this.activeBucketType][this.period]
        : null;
    */
    const lastDP = this.graphData?.percentages[this.activeBucketType]?.length;
    this.simpleYearlyProfit = lastDP ? this.graphData!.percentages[this.activeBucketType]![lastDP - 1].value : null;

    //this.fetchGraphData();
  };
  @action setPercentage = (value: number) => (this.percentage = value);

  fetchData = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<AccountEditPlan>(
      API_ACCOUNT_DETAIL_PLAN_HISTORY(this.accountId),
    );
    if (response && response.data) {
      this.data = response.data;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  fetchGraphData = async (first?: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<DevelopmentGraphData>(
      API_ACCOUNT_DETAIL_HISTORY(this.accountId, this.period),
    );
    if (response && response.data) {
      this.graphData = response.data;
      //this.graphData = mock.data;

      /*
        this.graphData.targets.total = [{value: {value: 20000, currency: Currency.CZK}, time: "2022-04-19T07:45:01.623+02:00"}];
        this.graphData.withdrawals.total =  [{value: {value: 50000, currency: Currency.CZK}, time: "2022-09-26T07:45:01.337+02:00"}];
        this.graphData.singleDeposits.total = [{value: {value: 5000, currency: Currency.CZK}, time: "2023-01-16T07:45:01.033+01:00"}];
        this.graphData.periodicDeposits.total = [{value: {value: 15000, currency: Currency.CZK}, time: "2024-03-13T07:45:00+01:00"}];
      */

      this.investments =
        this.graphData && Array.isArray(this.graphData.investments[this.activeBucketType])
          ? this.graphData.investments[this.activeBucketType]![
              this.graphData.investments[this.activeBucketType]!.length - 1
            ].value.value
          : 0;

      this.yearlyProfit = this.graphData?.yearlyProfit ? this.graphData.yearlyProfit[this.activeBucketType] : null;

      if (first && this.data) {
        this.activeBuckets =
          this.data.portfolioOnly && !this.graphData!.withHistory ? [] : this.data.buckets.slice().reverse();

        this.bucketTabs = this.activeBuckets.map((bucket) => {
          return {
            type: bucket.type,
            title: bucket.title,
            balance: { value: bucket.balance.value, currency: bucket.balance.currency },
            profit: bucket.accountDetailData.currentRevenue.value,
            active: true,
            subtitle: '',
          };
        });
        this.bucketTabs.unshift({
          type: BucketType.TOTAL,
          title: 'accounts_portfolioTotal',
          balance: { value: this.data.balance.value, currency: Currency.NONE },
          active: true,
          subtitle: '',
        });
      }
      this.balance = this.bucketTabs[this.activeBucket]?.balance?.value || 0;
    }
    this.dataReady = true;
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @computed
  get pie(): PieSeriesData {
    if (!this.data) return {};

    return convertPiechartData(
      this.activeBuckets[this.activeBucket - 1]?.portfolio?.chart?.dataset,
      this.activeBuckets[this.activeBucket - 1].portfolio.code,
      this.activeBuckets[this.activeBucket - 1].type,
    );
  }
}
